import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Trash2 } from 'lucide-react';

import {
    addFCTransferLineItem,
    mapTransferIdToApiPathParam,
    removeFCTransferLineItem,
} from 'api/warehouse/fcTransfers';
import { formatPurchaseOrderDate } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import {
    formatNumber,
    normalizeOptionalText,
} from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';
import {
    FCTransferLotPickerRow,
    getFCTransferInventoryRowKey,
} from 'components/warehouse/components/fcTransfers/create/FCTransferLotPickerRow';
import { SelectedTransferLotSummary } from 'components/warehouse/components/fcTransfers/create/SelectedTransferLotSummary';
import { FCTransferItemsTableCard } from 'components/warehouse/components/fcTransfers/entityView/FCTransferItemsTableCard';
import {
    FC_TRANSFER_DISPLAY_FALLBACK,
    isFCTransferPendingStatus,
} from 'components/warehouse/components/fcTransfers/fcTransferDisplayUtils';
import {
    defaultQuantitiesFromInventoryRow,
    newFCTransferLineItemClientId,
    readLotAndLocationIds,
} from 'components/warehouse/components/fcTransfers/fcTransferLineItemUtils';
import {
    fcTransferQueryKeys,
    useAvailableTransferLots,
} from 'components/warehouse/components/fcTransfers/queries/FCTransferQueries';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from 'shadcn/components/ui/Card';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';

/**
 * @param {Record<string, unknown>} item
 * @returns {string | null}
 */
function getTransferItemLotLocationKey(item) {
    if (item == null || typeof item !== 'object') return null;
    const lotRaw = item.lotId ?? item.lot_id;
    const locRaw =
        item.reservedFromLocationId ??
        item.reserved_from_location_id ??
        item.warehouseLocationId ??
        item.warehouse_location_id ??
        item.locationId ??
        item.location_id;
    const lotId = typeof lotRaw === 'number' ? lotRaw : parseInt(String(lotRaw ?? ''), 10);
    const locationId = typeof locRaw === 'number' ? locRaw : parseInt(String(locRaw ?? ''), 10);
    if (!Number.isFinite(lotId) || lotId <= 0) return null;
    if (!Number.isFinite(locationId) || locationId <= 0) return null;
    return `${lotId}:${locationId}`;
}

/**
 * @param {Record<string, unknown> | null | undefined} item
 * @returns {number | undefined}
 */
function getPersistedTransferItemId(item) {
    return mapTransferIdToApiPathParam(item?.id ?? item?.transferItemId);
}

/**
 * @param {Record<string, unknown>} snap
 * @returns {number | undefined}
 */
function readAvailableCasesFromSnapshot(snap) {
    const n = snap?.numCases ?? snap?.num_cases;
    if (n === undefined || n === null || n === '') return undefined;
    const parsed = Number(n);
    return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : undefined;
}

/**
 * @param {string | number} raw
 * @returns {number}
 */
function parseNonNegativeInt(raw) {
    if (raw === '' || raw === undefined || raw === null) return 0;
    const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
    if (!Number.isFinite(n) || n < 0) return 0;
    return Math.floor(n);
}

/**
 * @param {Record<string, unknown>} row
 * @returns {string | number}
 */
function formatCasesCell(row) {
    const n = row?.numCases ?? row?.num_cases;
    if (n === undefined || n === null || n === '') return FC_TRANSFER_DISPLAY_FALLBACK;
    return formatNumber(n, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
}

/**
 * @param {Record<string, unknown>} row
 * @returns {string | number}
 */
function formatLooseCell(row) {
    const n = row?.numLooseUnits ?? row?.num_loose_units;
    if (n === undefined || n === null || n === '') return FC_TRANSFER_DISPLAY_FALLBACK;
    return formatNumber(n, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
}

/**
 * @param {Object} props
 * @param {boolean} props.isStagedRemoved
 * @param {boolean} props.mutationBusy
 * @param {(id: number) => void} props.onToggleRemove
 * @param {Record<string, unknown>} props.row
 */
function FCTransferEntityLineItemRemoveCell({ isStagedRemoved, mutationBusy, onToggleRemove, row }) {
    const rawId = row?.id ?? row?.transferItemId;
    const itemId = mapTransferIdToApiPathParam(rawId);
    const disabled = mutationBusy || itemId === undefined;
    return (
        <Button
            type="button"
            variant={isStagedRemoved ? 'destructive' : 'ghost'}
            size="icon"
            className={isStagedRemoved ? '' : 'text-muted-foreground hover:text-destructive'}
            disabled={disabled}
            aria-label={isStagedRemoved ? 'Unmark removal' : 'Mark for removal'}
            onClick={() => {
                if (itemId !== undefined) onToggleRemove(itemId);
            }}
        >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
        </Button>
    );
}

FCTransferEntityLineItemRemoveCell.displayName = 'FCTransferEntityLineItemRemoveCell';

/**
 * @param {import('@tanstack/react-table').CellContext<Record<string, unknown>, Record<string, unknown>>} ctx
 */
function renderEntityLineItemRemoveCell(ctx) {
    const row = ctx.getValue();
    const meta = ctx.table.options.meta ?? {};
    const mutationBusy = Boolean(meta.mutationBusy);
    const { isPersistedRowStagedRemoved, onTogglePersistedRemove } = meta;
    if (typeof onTogglePersistedRemove !== 'function') {
        return null;
    }
    const isStagedRemoved =
        typeof isPersistedRowStagedRemoved === 'function' ? isPersistedRowStagedRemoved(row) : false;
    return (
        <FCTransferEntityLineItemRemoveCell
            isStagedRemoved={isStagedRemoved}
            mutationBusy={mutationBusy}
            row={row}
            onToggleRemove={onTogglePersistedRemove}
        />
    );
}

/**
 * @param {import('@tanstack/react-table').CellContext<Record<string, unknown>, Record<string, unknown>>} ctx
 */
function renderEntityLineItemActionsCell(ctx) {
    const row = ctx.getValue();
    if (row != null && typeof row === 'object' && row.entityRowKind === 'staged') {
        const meta = ctx.table.options.meta ?? {};
        const mutationBusy = Boolean(meta.mutationBusy);
        return (
            <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-muted-foreground hover:text-destructive"
                disabled={mutationBusy}
                aria-label="Remove staged line item"
                onClick={() => {
                    if (typeof meta.onRemoveStagedRow === 'function' && row.stagedLineClientId != null) {
                        meta.onRemoveStagedRow(String(row.stagedLineClientId));
                    }
                }}
            >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
            </Button>
        );
    }
    return renderEntityLineItemRemoveCell(ctx);
}

/** @type {import('@tanstack/react-table').ColumnDef<Record<string, unknown>, unknown>[]} */
const FC_TRANSFER_ENTITY_LINE_ITEM_EDIT_COLUMNS = [
    {
        id: 'itemName',
        header: 'Item',
        accessorFn: (row) => row?.itemName ?? row?.item_name,
        cell: ({ getValue }) => normalizeOptionalText(getValue()),
    },
    {
        id: 'externalSku',
        header: 'External SKU',
        accessorFn: (row) => row?.externalSku ?? row?.external_sku,
        cell: ({ getValue }) => normalizeOptionalText(getValue()),
    },
    {
        id: 'lotId',
        header: 'Lot ID',
        accessorFn: (row) => row?.lotId ?? row?.lot_id,
        cell: ({ getValue }) => normalizeOptionalText(getValue()),
    },
    {
        id: 'bbd',
        header: 'BBD',
        accessorFn: (row) => row?.sourceExpiresOnSnapshot ?? row?.source_expires_on_snapshot,
        cell: ({ getValue }) => formatPurchaseOrderDate(getValue(), FC_TRANSFER_DISPLAY_FALLBACK),
    },
    {
        id: 'unitsPerCase',
        header: 'Units / case',
        accessorFn: (row) => row?.sourceUnitsPerCaseSnapshot ?? row?.source_units_per_case_snapshot,
        cell: ({ getValue }) => {
            const v = getValue();
            if (v === undefined || v === null || v === '') return FC_TRANSFER_DISPLAY_FALLBACK;
            return formatNumber(v, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
        },
    },
    {
        id: 'numCases',
        header: 'Quantity (cases)',
        accessorFn: (row) => row,
        cell: ({ getValue, table }) => {
            const row = getValue();
            if (row != null && typeof row === 'object' && row.entityRowKind === 'staged') {
                const meta = table.options.meta ?? {};
                const snap = row.stagedInventorySnapshot ?? {};
                const maxCases = readAvailableCasesFromSnapshot(snap);
                return (
                    <Input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={maxCases !== undefined ? maxCases : undefined}
                        disabled={Boolean(meta.mutationBusy)}
                        className="w-32 min-w-[8rem] tabular-nums"
                        aria-label="Quantity in cases"
                        value={Number.isFinite(Number(row.numCases)) ? String(row.numCases) : '0'}
                        onChange={(e) =>
                            meta.onStagedNumCasesChange?.(row.stagedLineClientId, parseNonNegativeInt(e.target.value))
                        }
                    />
                );
            }
            return formatCasesCell(row);
        },
    },
    {
        id: 'numLooseUnits',
        header: 'Loose units',
        accessorFn: (row) => row,
        cell: ({ getValue, table }) => {
            const row = getValue();
            if (row != null && typeof row === 'object' && row.entityRowKind === 'staged') {
                const meta = table.options.meta ?? {};
                return (
                    <Input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        disabled={Boolean(meta.mutationBusy)}
                        className="w-32 min-w-[8rem] tabular-nums"
                        aria-label="Loose units"
                        value={Number.isFinite(Number(row.numLooseUnits)) ? String(row.numLooseUnits) : '0'}
                        onChange={(e) =>
                            meta.onStagedNumLooseUnitsChange?.(
                                row.stagedLineClientId,
                                parseNonNegativeInt(e.target.value),
                            )
                        }
                    />
                );
            }
            return formatLooseCell(row);
        },
    },
    {
        id: 'status',
        header: 'Line status',
        accessorFn: (row) => row?.status,
        cell: ({ getValue }) => normalizeOptionalText(getValue()),
    },
    {
        id: 'actions',
        header: '',
        accessorFn: (row) => row,
        cell: renderEntityLineItemActionsCell,
    },
];

/**
 * Line items: read-only by default. In edit mode (PENDING), changes are staged; Save applies removes then adds (PO V2–style).
 *
 * @param {Object} props
 * @param {Record<string, unknown>} props.transfer
 * @param {Array<Record<string, unknown>>} [props.items=[]]
 * @param {boolean} [props.editMode=false]
 * @param {boolean} [props.isSavingLineItems=false]
 * @param {(dirty: boolean) => void} [props.onStagingDirtyChange]
 * @param {boolean} [props.canEditLineItemsAtSource=true] - When false, line-item edit UI is read-only (operator not at source).
 * @param {string | number | null | undefined} [props.operatorFacilityId] - Logged-in facility; lots query uses this when it matches the transfer source (same as create flow).
 */
export const FCTransferEntityLineItemsEditor = forwardRef(
    (
        {
            canEditLineItemsAtSource = true,
            editMode = false,
            isSavingLineItems = false,
            items = [],
            onStagingDirtyChange,
            operatorFacilityId: operatorFacilityIdProp,
            transfer,
        },
        ref,
    ) => {
        const queryClient = useQueryClient();
        const canMutate = isFCTransferPendingStatus(transfer);
        const transferId = transfer?.id ?? transfer?.transferId;
        const sourceFacilityRaw = transfer?.sourceFacilityId ?? transfer?.source_facility_id;
        const sourceFacilityId = useMemo(() => {
            if (sourceFacilityRaw === undefined || sourceFacilityRaw === null || sourceFacilityRaw === '') return null;
            const n = Number(sourceFacilityRaw);
            return Number.isFinite(n) && n > 0 ? n : null;
        }, [sourceFacilityRaw]);

        const operatorFacilityId = useMemo(() => {
            if (
                operatorFacilityIdProp === undefined ||
                operatorFacilityIdProp === null ||
                operatorFacilityIdProp === ''
            ) {
                return null;
            }
            const n = Number(operatorFacilityIdProp);
            return Number.isFinite(n) && n > 0 ? n : null;
        }, [operatorFacilityIdProp]);

        const sourceMatchesOperator =
            operatorFacilityId != null && sourceFacilityId != null && operatorFacilityId === sourceFacilityId;

        const lotsQueryFacilityId = sourceMatchesOperator ? operatorFacilityId : null;

        const [selectedInventoryRow, setSelectedInventoryRow] = useState(
            /** @type {Record<string, unknown> | null} */ (null),
        );
        const [draftNumCases, setDraftNumCases] = useState(0);
        const [draftNumLooseUnits, setDraftNumLooseUnits] = useState(0);
        const [lineItemsError, setLineItemsError] = useState(/** @type {string | null} */ (null));
        const [saveError, setSaveError] = useState(/** @type {string | null} */ (null));
        const [stagedRemovedIds, setStagedRemovedIds] = useState(() => new Set());
        const [stagedAdds, setStagedAdds] = useState(/** @type {Array<Record<string, unknown>>} */ ([]));

        const { data: availableRows = [], isFetching: isLotsLoading } = useAvailableTransferLots({
            facilityId: lotsQueryFacilityId,
            enabled: Boolean(lotsQueryFacilityId) && canMutate && editMode && canEditLineItemsAtSource,
        });

        const persistedItemsForTable = useMemo(() => (Array.isArray(items) ? items : []), [items]);

        const combinedTableRows = useMemo(() => {
            const persisted = persistedItemsForTable.map((item) => ({
                ...item,
                entityRowKind: /** @type {const} */ ('persisted'),
            }));
            const staged = stagedAdds.map((li) => {
                const snap = li.inventorySnapshot ?? {};
                return {
                    entityRowKind: /** @type {const} */ ('staged'),
                    stagedLineClientId: li.clientId,
                    stagedInventorySnapshot: li.inventorySnapshot,
                    itemName: snap.itemName ?? snap.item_name,
                    externalSku: snap.externalSku ?? snap.external_sku,
                    lotId: li.lotId,
                    sourceExpiresOnSnapshot: snap.expiresOn ?? snap.expires_on ?? snap.sourceExpiresOnSnapshot,
                    sourceUnitsPerCaseSnapshot:
                        snap.unitsPerCase ?? snap.units_per_case ?? snap.sourceUnitsPerCaseSnapshot,
                    numCases: li.numCases,
                    numLooseUnits: li.numLooseUnits,
                    status: 'New',
                };
            });
            return [...persisted, ...staged];
        }, [persistedItemsForTable, stagedAdds]);

        const addedLotLocationKeys = useMemo(() => {
            const s = new Set();
            for (const item of persistedItemsForTable) {
                const pid = getPersistedTransferItemId(item);
                const isStagedRemove = pid !== undefined && stagedRemovedIds.has(String(pid));
                if (!isStagedRemove) {
                    const k = getTransferItemLotLocationKey(item);
                    if (k) s.add(k);
                }
            }
            for (const li of stagedAdds) {
                s.add(`${Number(li.lotId)}:${Number(li.locationId)}`);
            }
            return s;
        }, [persistedItemsForTable, stagedAdds, stagedRemovedIds]);

        const pickableInventoryRows = useMemo(() => {
            if (!Array.isArray(availableRows)) return [];
            return availableRows.filter((row) => {
                const k = getFCTransferInventoryRowKey(row);
                if (!k) return false;
                return !addedLotLocationKeys.has(k);
            });
        }, [addedLotLocationKeys, availableRows]);

        const stagingDirty = stagedRemovedIds.size > 0 || stagedAdds.length > 0;

        useEffect(() => {
            onStagingDirtyChange?.(stagingDirty);
        }, [onStagingDirtyChange, stagingDirty]);

        const handleSelectedInventoryRowChange = useCallback((row) => {
            setSelectedInventoryRow(row);
            if (row != null && typeof row === 'object') {
                const d = defaultQuantitiesFromInventoryRow(row);
                setDraftNumCases(d.numCases);
                setDraftNumLooseUnits(d.numLooseUnits);
            }
        }, []);

        const resetStaging = useCallback(() => {
            setStagedRemovedIds(new Set());
            setStagedAdds([]);
            setSelectedInventoryRow(null);
            setLineItemsError(null);
            setSaveError(null);
            setDraftNumCases(0);
            setDraftNumLooseUnits(0);
        }, []);

        useEffect(() => {
            if (!editMode || !canMutate) {
                resetStaging();
            }
        }, [canMutate, editMode, resetStaging]);

        useEffect(() => {
            if (selectedInventoryRow == null) return;
            const k = getFCTransferInventoryRowKey(selectedInventoryRow);
            if (!k || !addedLotLocationKeys.has(k)) return;
            setSelectedInventoryRow(null);
        }, [addedLotLocationKeys, selectedInventoryRow]);

        const isPersistedRowStagedRemoved = useCallback(
            (item) => {
                const pid = getPersistedTransferItemId(item);
                return pid !== undefined && stagedRemovedIds.has(String(pid));
            },
            [stagedRemovedIds],
        );

        const handleToggleStageRemovePersisted = useCallback((transferItemId) => {
            setSaveError(null);
            setStagedRemovedIds((prev) => {
                const next = new Set(prev);
                const key = String(transferItemId);
                if (next.has(key)) {
                    next.delete(key);
                } else {
                    next.add(key);
                }
                return next;
            });
        }, []);

        const saveStagedChanges = useCallback(async () => {
            setSaveError(null);
            if (stagedRemovedIds.size === 0 && stagedAdds.length === 0) {
                return false;
            }

            const invalidQty = stagedAdds.some((li) => {
                const c = Math.max(0, Math.floor(Number(li.numCases) || 0));
                const u = Math.max(0, Math.floor(Number(li.numLooseUnits) || 0));
                return c <= 0 && u <= 0;
            });
            if (invalidQty) {
                const msg = 'Each new line needs at least one case or one loose unit before saving.';
                setSaveError(msg);
                throw new Error(msg);
            }

            const seenPairs = new Set();
            for (const li of stagedAdds) {
                const pairKey = `${Number(li.lotId)}:${Number(li.locationId)}`;
                if (seenPairs.has(pairKey)) {
                    const msg = 'Remove duplicate staged lot and location rows before saving.';
                    setSaveError(msg);
                    throw new Error(msg);
                }
                seenPairs.add(pairKey);
            }

            try {
                const removePairs = [...stagedRemovedIds]
                    .map((idStr) => ({
                        idStr: String(idStr),
                        apiId: mapTransferIdToApiPathParam(idStr),
                    }))
                    .filter((p) => p.apiId !== undefined);

                const removeResponses = await Promise.all(
                    removePairs.map((p) => removeFCTransferLineItem(transferId, p.apiId)),
                );
                const firstFailedRemove = removeResponses.findIndex((r) => !r?.status);
                if (firstFailedRemove !== -1) {
                    setStagedRemovedIds((prev) => {
                        const next = new Set(prev);
                        for (let i = 0; i < firstFailedRemove; i += 1) {
                            next.delete(removePairs[i].idStr);
                        }
                        return next;
                    });
                    const msg = removeResponses[firstFailedRemove]?.msg ?? 'Failed to remove a line item';
                    setSaveError(msg);
                    throw new Error(msg);
                }

                // Removals that reached the server succeeded — do not re-send those IDs if adds fail later.
                setStagedRemovedIds(new Set());

                const addResponses = await Promise.all(
                    stagedAdds.map((li) =>
                        addFCTransferLineItem(transferId, {
                            lotId: li.lotId,
                            locationId: li.locationId,
                            numCases: Math.max(0, Math.floor(Number(li.numCases) || 0)),
                            numLooseUnits: Math.max(0, Math.floor(Number(li.numLooseUnits) || 0)),
                        }),
                    ),
                );
                const firstFailedAdd = addResponses.findIndex((r) => !r?.status);
                if (firstFailedAdd !== -1) {
                    setStagedAdds((prev) => prev.slice(firstFailedAdd));
                    const msg = addResponses[firstFailedAdd]?.msg ?? 'Failed to add a line item';
                    setSaveError(msg);
                    throw new Error(msg);
                }

                await queryClient.invalidateQueries({ queryKey: fcTransferQueryKeys.all });
                resetStaging();
                return true;
            } catch (err) {
                await queryClient.invalidateQueries({ queryKey: fcTransferQueryKeys.all });
                const msg = err?.message ?? 'Failed to save line item changes';
                setSaveError(msg);
                throw err;
            }
        }, [queryClient, resetStaging, stagedAdds, stagedRemovedIds, transferId]);

        useImperativeHandle(
            ref,
            () => ({
                resetStaging,
                saveStagedChanges,
            }),
            [resetStaging, saveStagedChanges],
        );

        const handleAddLotToStaging = useCallback(() => {
            if (selectedInventoryRow == null) return;
            const ids = readLotAndLocationIds(selectedInventoryRow);
            if (!ids) {
                setLineItemsError('Select a lot with a valid warehouse location.');
                return;
            }
            const pairKey = `${ids.lotId}:${ids.locationId}`;
            if (addedLotLocationKeys.has(pairKey)) {
                setLineItemsError('This lot and location are already on the transfer or staged.');
                return;
            }
            const numCases = Math.max(0, Math.floor(Number(draftNumCases) || 0));
            const numLooseUnits = Math.max(0, Math.floor(Number(draftNumLooseUnits) || 0));
            if (numCases <= 0 && numLooseUnits <= 0) {
                setLineItemsError('Enter at least one case or one loose unit to add.');
                return;
            }
            setLineItemsError(null);
            setSaveError(null);
            setStagedAdds((prev) => [
                ...prev,
                {
                    clientId: newFCTransferLineItemClientId('entity'),
                    lotId: ids.lotId,
                    locationId: ids.locationId,
                    inventorySnapshot: { ...selectedInventoryRow },
                    numCases,
                    numLooseUnits,
                },
            ]);
            setSelectedInventoryRow(null);
        }, [addedLotLocationKeys, draftNumCases, draftNumLooseUnits, selectedInventoryRow]);

        const handleNumCasesChange = useCallback((clientId, numCases) => {
            setStagedAdds((prev) => prev.map((row) => (row.clientId === clientId ? { ...row, numCases } : row)));
        }, []);

        const handleNumLooseUnitsChange = useCallback((clientId, numLooseUnits) => {
            setStagedAdds((prev) => prev.map((row) => (row.clientId === clientId ? { ...row, numLooseUnits } : row)));
        }, []);

        const handleRemoveStagedRow = useCallback((clientId) => {
            setStagedAdds((prev) => prev.filter((row) => row.clientId !== clientId));
        }, []);

        const controlsDisabled = isSavingLineItems;

        const locationBadges = useMemo(() => {
            if (selectedInventoryRow == null || !Array.isArray(availableRows)) return undefined;
            const lotId = selectedInventoryRow.lotId ?? selectedInventoryRow.lot_id;
            if (lotId === undefined || lotId === null) return undefined;
            const lotKey = String(lotId);
            const matches = availableRows.filter((r) => String(r.lotId ?? r.lot_id ?? '') === lotKey);
            if (matches.length <= 1) return undefined;
            const filtered = matches.filter((r) => {
                const rowKey = getFCTransferInventoryRowKey(r);
                return Boolean(rowKey) && !addedLotLocationKeys.has(rowKey);
            });
            if (filtered.length <= 1) return undefined;
            return filtered.map((r, i) => ({
                id: String(r.warehouseLocationId ?? r.warehouse_location_id ?? i),
                label: String(r.locationName ?? r.location_name ?? 'Location'),
                numCases: r.numCases ?? r.num_cases ?? 0,
            }));
        }, [addedLotLocationKeys, availableRows, selectedInventoryRow]);

        const editTable = useReactTable({
            data: combinedTableRows,
            columns: FC_TRANSFER_ENTITY_LINE_ITEM_EDIT_COLUMNS,
            getCoreRowModel: getCoreRowModel(),
            getRowId: (row, index) => {
                if (
                    row != null &&
                    typeof row === 'object' &&
                    row.entityRowKind === 'staged' &&
                    row.stagedLineClientId != null
                ) {
                    return `staged-${row.stagedLineClientId}`;
                }
                return String(row?.id ?? `fc-transfer-entity-item-${index}`);
            },
            meta: {
                mutationBusy: controlsDisabled,
                isPersistedRowStagedRemoved,
                onTogglePersistedRemove: handleToggleStageRemovePersisted,
                onStagedNumCasesChange: handleNumCasesChange,
                onStagedNumLooseUnitsChange: handleNumLooseUnitsChange,
                onRemoveStagedRow: handleRemoveStagedRow,
            },
        });

        const lotPickerEmptyHelperText = useMemo(() => {
            if (!sourceFacilityId) {
                return 'This transfer has no source facility; available lots cannot be loaded.';
            }
            if (!sourceMatchesOperator) {
                return 'Available lots are loaded for your facility when you are the transfer source.';
            }
            const total = Array.isArray(availableRows) ? availableRows.length : 0;
            if (total > 0 && pickableInventoryRows.length === 0) {
                return 'All eligible lot and location rows for this facility are already on the transfer or staged. Remove a staged row or cancel a removal to add a different lot.';
            }
            return 'No eligible inventory rows for this source facility.';
        }, [availableRows, pickableInventoryRows.length, sourceFacilityId, sourceMatchesOperator]);

        if (!canMutate || !editMode || !canEditLineItemsAtSource) {
            return <FCTransferItemsTableCard items={items} />;
        }

        return (
            <Card className="shadow-sm">
                <CardHeader className="pb-0">
                    <CardTitle className="text-base font-medium">Line items</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-6 pt-6">
                    {saveError ? (
                        <Alert variant="destructive">
                            <AlertTitle>Could not save line items</AlertTitle>
                            <AlertDescription>{saveError}</AlertDescription>
                        </Alert>
                    ) : null}
                    <FCTransferLotPickerRow
                        rows={pickableInventoryRows}
                        selectedRow={selectedInventoryRow}
                        onSelectedRowChange={handleSelectedInventoryRowChange}
                        onAddLot={handleAddLotToStaging}
                        isLoading={Boolean(lotsQueryFacilityId) && isLotsLoading}
                        addLotDisabled={controlsDisabled}
                        emptyHelperText={lotPickerEmptyHelperText}
                    />
                    <SelectedTransferLotSummary selectedRow={selectedInventoryRow} locationBadges={locationBadges} />
                    {selectedInventoryRow ? (
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="flex flex-col gap-2">
                                <Label htmlFor="fc-entity-add-cases">Quantity (cases) to add</Label>
                                <Input
                                    id="fc-entity-add-cases"
                                    type="number"
                                    inputMode="numeric"
                                    min={0}
                                    disabled={controlsDisabled}
                                    className="tabular-nums"
                                    value={String(draftNumCases)}
                                    onChange={(e) =>
                                        setDraftNumCases(Math.max(0, Math.floor(Number(e.target.value) || 0)))
                                    }
                                />
                            </div>
                            <div className="flex flex-col gap-2">
                                <Label htmlFor="fc-entity-add-loose">Loose units to add</Label>
                                <Input
                                    id="fc-entity-add-loose"
                                    type="number"
                                    inputMode="numeric"
                                    min={0}
                                    disabled={controlsDisabled}
                                    className="tabular-nums"
                                    value={String(draftNumLooseUnits)}
                                    onChange={(e) =>
                                        setDraftNumLooseUnits(Math.max(0, Math.floor(Number(e.target.value) || 0)))
                                    }
                                />
                            </div>
                        </div>
                    ) : null}
                    {lineItemsError ? (
                        <p className="text-sm text-destructive" role="alert">
                            {lineItemsError}
                        </p>
                    ) : null}
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                {editTable.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <TableHead key={header.id} className="whitespace-nowrap text-left">
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(header.column.columnDef.header, header.getContext())}
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {editTable.getRowModel().rows.length ? (
                                    editTable.getRowModel().rows.map((row) => {
                                        const isStagedNew =
                                            row.original != null &&
                                            typeof row.original === 'object' &&
                                            row.original.entityRowKind === 'staged';
                                        const stagedRemove = !isStagedNew && isPersistedRowStagedRemoved(row.original);
                                        let rowClassName;
                                        if (stagedRemove) {
                                            rowClassName =
                                                'bg-muted/30 [&_td:not(:last-child)]:line-through [&_td:not(:last-child)]:opacity-60';
                                        } else if (isStagedNew) {
                                            rowClassName = 'bg-muted/20';
                                        } else {
                                            rowClassName = undefined;
                                        }
                                        return (
                                            <TableRow key={row.id} className={rowClassName}>
                                                {row.getVisibleCells().map((cell) => (
                                                    <TableCell
                                                        key={cell.id}
                                                        className="whitespace-normal break-words text-left"
                                                    >
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell
                                            colSpan={FC_TRANSFER_ENTITY_LINE_ITEM_EDIT_COLUMNS.length}
                                            className="h-24 text-center text-muted-foreground"
                                        >
                                            No line items on this transfer.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
        );
    },
);

FCTransferEntityLineItemsEditor.displayName = 'FCTransferEntityLineItemsEditor';
